import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bike, Eye, EyeOff, AlertCircle } from "lucide-react";

export default function Login() {
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [verPassword, setVerPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { usuario, password });
      login(data.usuario);
      navigate("/");
    } catch (err) {
      const msg = err.response ? errorMsg(err) : "No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.";
      setError(msg);
      toast.error(msg, { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="flex items-center gap-2 mb-8">
            <div className="bg-primary text-primary-foreground p-2 rounded-sm"><Bike size={24} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Nextep Taller</h1>
              <p className="text-xs text-muted-foreground">Cotizaciones de reparación de motocicletas</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="usuario">Usuario</Label>
            <Input id="usuario" data-testid="login-usuario-input" value={usuario}
              autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username"
              onChange={(e) => setUsuario(e.target.value)} placeholder="Tu usuario" autoFocus required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Contraseña</Label>
            <div className="relative">
              <Input id="password" data-testid="login-password-input" type={verPassword ? "text" : "password"}
                value={password} autoComplete="current-password" className="pr-10"
                onChange={(e) => setPassword(e.target.value)} placeholder="Tu contraseña" required />
              <button type="button" data-testid="toggle-ver-password"
                aria-label={verPassword ? "Ocultar contraseña" : "Ver contraseña"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setVerPassword((v) => !v)}>
                {verPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <div data-testid="login-error" role="alert"
              className="flex items-start gap-2 text-sm text-destructive bg-red-50 border border-red-200 rounded-sm px-3 py-2.5">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <Button data-testid="login-submit-btn" type="submit" className="w-full" disabled={loading}>
            {loading ? "Ingresando..." : "Iniciar sesión"}
          </Button>
          <Link to="/olvide-password" data-testid="olvido-password-link"
            className="block text-center text-xs text-muted-foreground hover:text-foreground">
            ¿Olvidó su contraseña?
          </Link>
        </form>
      </div>
      <div className="hidden lg:block flex-1 relative">
        <img src="https://images.unsplash.com/photo-1549636367-13c144c47063?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Taller de motocicletas" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-900/40" />
        <div className="absolute bottom-10 left-10 text-white max-w-md">
          <h2 className="font-heading text-3xl font-bold mb-2">Tu taller, organizado</h2>
          <p className="text-sm text-white/80">Catálogo de repuestos, plantillas de reparación y cotizaciones con asistente de IA.</p>
        </div>
      </div>
    </div>
  );
}
